import * as React from 'react'
import type { InputProps, InputRef } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import debounce from 'lodash/debounce'
import { useLocation } from 'react-router-dom'
import { useI18n } from 'context/I18nProvider'
import Input from 'components/UI/Input'

type SearchInputProps = Omit<InputProps, 'onChange'> & {
  onChange: (txt: string) => void
  withSearchIcon?: boolean
  minCharsSearch?: number
}

const SearchInput = React.forwardRef<InputRef, SearchInputProps>((props, ref) => {
  const { onChange, value, defaultValue, withSearchIcon = true, minCharsSearch = 0, ...rest } = props
  const { i18n } = useI18n()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const search = searchParams.get('q')

  const [showHelp, setShowHelp] = React.useState(false)

  const debouncedSearch = React.useMemo(() => (
    debounce((text: string) => {
      onChange?.(text)
    }, 300)
  ), [onChange])

  const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value
    if (val.length === 0) {
      setShowHelp(false)
      debouncedSearch('')
    } else if (val.length < minCharsSearch) {
      setShowHelp(true)
    } else {
      setShowHelp(false)
      debouncedSearch(val)
    }
  }, [debouncedSearch, minCharsSearch])

  return (
    <>
      <Input
        ref={ref}
        defaultValue={search || ''}
        addonBefore={withSearchIcon && <SearchOutlined className="text-lg" />}
        onChange={handleChange}
        placeholder={i18n('label.searchplaceholder')}
        allowClear
        outline={false}
        {...rest}
      />
      {showHelp && (
        <div style={{ color: '#faad14', fontSize: 12, marginTop: 4 }}>
          {i18n('label.minCharsSearch', { minCharsSearch })}
        </div>
      )}
    </>
  )
})
export default SearchInput
